import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Pencil, Trash2, Power, X, FileSignature } from "lucide-react";
import DataTable from "../../components/DataTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  listSigningVendors,
  createSigningVendor,
  updateSigningVendor,
  deleteSigningVendor,
} from "../../services/signingVendorAdmin";

const EMPTY = {
  displayName: "",
  providerKey: "",
  webhookPathSlug: "",
  baseUrl: "",
  clientId: "",
  clientSecret: "",
  webhookSecret: "",
  isSandbox: false,
  isActive: true,
  priority: 0,
};

const Field = ({ label, value, onChange, required, placeholder, type = "text", hint }) => (
  <div>
    <label className="text-xs font-medium text-slate-600">
      {label} {required && <span className="text-flamingo-600">*</span>}
    </label>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
    />
    {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
  </div>
);

const CheckField = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 mt-6 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500/40"
    />
    <span className="text-sm font-medium text-slate-700">{label}</span>
  </label>
);

const SigningVendors = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // vendor row being edited
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // {id, name}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listSigningVendors(true);
      setRows(res?.items || (Array.isArray(res) ? res : []));
    } catch (e) {
      toast.error("Failed to load signing vendors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalOpen(true);
  };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      displayName: v.displayName || "",
      providerKey: v.providerKey || "",
      webhookPathSlug: v.webhookPathSlug || "",
      baseUrl: v.baseUrl || "",
      clientId: v.clientId || "",
      clientSecret: "", // secrets are never returned — leave blank to keep existing
      webhookSecret: "",
      isSandbox: !!v.isSandbox,
      isActive: !!v.isActive,
      priority: v.priority ?? 0,
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (
      !form.displayName?.trim() ||
      !form.providerKey?.trim() ||
      !form.webhookPathSlug?.trim()
    ) {
      return toast.error("Display name, provider key and webhook path slug are required");
    }
    setSaving(true);
    try {
      // Build payload; omit empty secrets so we never overwrite existing ones with blanks.
      const payload = {
        displayName: form.displayName,
        providerKey: form.providerKey,
        webhookPathSlug: form.webhookPathSlug,
        baseUrl: form.baseUrl,
        clientId: form.clientId,
        isSandbox: !!form.isSandbox,
        isActive: !!form.isActive,
        priority: Number(form.priority) || 0,
      };
      if (form.clientSecret?.trim()) payload.clientSecret = form.clientSecret;
      if (form.webhookSecret?.trim()) payload.webhookSecret = form.webhookSecret;

      if (editing) {
        await updateSigningVendor(editing.esignVendorId, payload);
        toast.success("Signing vendor updated");
      } else {
        await createSigningVendor(payload);
        toast.success("Signing vendor created");
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save signing vendor");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (v) => {
    try {
      await updateSigningVendor(v.esignVendorId, { isActive: !v.isActive });
      toast.success(v.isActive ? "Vendor deactivated" : "Vendor activated");
      await load();
    } catch (e) {
      toast.error("Failed to update vendor status");
    }
  };

  const doDelete = async () => {
    const id = confirm?.id;
    setConfirm(null);
    try {
      await deleteSigningVendor(id);
      toast.success("Signing vendor deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete signing vendor");
    }
  };

  const columns = [
    {
      key: "name",
      header: "Vendor",
      width: 260,
      render: (r) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{r.displayName}</p>
          <p className="text-xs text-slate-500">{r.providerKey}</p>
        </div>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      width: 100,
      render: (r) => <span className="text-sm text-slate-600">{r.priority ?? "—"}</span>,
    },
    {
      key: "sandbox",
      header: "Sandbox",
      width: 110,
      render: (r) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
            r.isSandbox
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-slate-50 text-slate-600 border-slate-200"
          }`}
        >
          {r.isSandbox ? "SANDBOX" : "LIVE"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Active",
      width: 120,
      render: (r) => (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
            r.isActive
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-rose-50 text-rose-700 border-rose-200"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${r.isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
          {r.isActive ? "ACTIVE" : "INACTIVE"}
        </span>
      ),
    },
    {
      key: "secrets",
      header: "Secrets",
      width: 180,
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${
              r.hasClientSecret
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-50 text-slate-400 border-slate-200"
            }`}
          >
            Client {r.hasClientSecret ? "✓" : "—"}
          </span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${
              r.hasWebhookSecret
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-50 text-slate-400 border-slate-200"
            }`}
          >
            Webhook {r.hasWebhookSecret ? "✓" : "—"}
          </span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: 140,
      align: "center",
      render: (r) => (
        <div className="flex items-center justify-center gap-1.5">
          <button
            title="Edit"
            onClick={() => openEdit(r)}
            className="w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 inline-flex items-center justify-center"
          >
            <Pencil size={15} />
          </button>
          <button
            title={r.isActive ? "Deactivate" : "Activate"}
            onClick={() => toggleActive(r)}
            className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${
              r.isActive ? "text-amber-500 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"
            }`}
          >
            <Power size={15} />
          </button>
          <button
            title="Delete"
            onClick={() => setConfirm({ id: r.esignVendorId, name: r.displayName })}
            className="w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className={embedded ? "" : "p-4 md:p-6 bg-slate-50 min-h-screen"}>
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        {embedded ? (
          <span />
        ) : (
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Signing Vendors</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Aadhaar eSign providers used to send documents for digital signature.
            </p>
          </div>
        )}
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
        >
          <Plus size={16} /> Add Vendor
        </button>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        minWidth={980}
        rowKey={(r, i) => r?.esignVendorId ?? i}
        emptyState={
          <div className="px-6 py-12 text-center">
            <FileSignature size={28} className="mx-auto text-slate-300" />
            <p className="text-base font-semibold text-slate-900 mt-2">No signing vendors yet</p>
            <p className="text-sm text-slate-500 mt-1">Add an eSign provider to enable document signing.</p>
          </div>
        }
      />

      {/* Add/Edit modal */}
      <div className={`fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 ${modalOpen ? "" : "hidden"}`}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">
              {editing ? "Edit Signing Vendor" : "Add Signing Vendor"}
            </h3>
            <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X size={18} />
            </button>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Display Name"
              value={form.displayName}
              onChange={(v) => setForm({ ...form, displayName: v })}
              required
              placeholder="Digio Production"
            />
            <Field
              label="Provider Key"
              value={form.providerKey}
              onChange={(v) => setForm({ ...form, providerKey: v })}
              required
              placeholder="digio"
              hint="e.g. mock, digio, leegality"
            />
            <Field
              label="Webhook Path Slug"
              value={form.webhookPathSlug}
              onChange={(v) => setForm({ ...form, webhookPathSlug: v })}
              required
              placeholder="digio-prod"
            />
            <Field
              label="Base URL"
              value={form.baseUrl}
              onChange={(v) => setForm({ ...form, baseUrl: v })}
              placeholder="https://api.digio.in"
            />
            <Field
              label="Client ID"
              value={form.clientId}
              onChange={(v) => setForm({ ...form, clientId: v })}
            />
            <Field
              label="Priority"
              type="number"
              value={form.priority}
              onChange={(v) => setForm({ ...form, priority: v })}
              placeholder="0"
            />
            <Field
              label="Client Secret"
              type="password"
              value={form.clientSecret}
              onChange={(v) => setForm({ ...form, clientSecret: v })}
              placeholder={editing ? "leave blank to keep existing" : ""}
              hint={editing && editing.hasClientSecret ? "A client secret is configured." : undefined}
            />
            <Field
              label="Webhook Secret"
              type="password"
              value={form.webhookSecret}
              onChange={(v) => setForm({ ...form, webhookSecret: v })}
              placeholder={editing ? "leave blank to keep existing" : ""}
              hint={editing && editing.hasWebhookSecret ? "A webhook secret is configured." : undefined}
            />
            <CheckField
              label="Sandbox mode"
              checked={form.isSandbox}
              onChange={(v) => setForm({ ...form, isSandbox: v })}
            />
            <CheckField
              label="Active"
              checked={form.isActive}
              onChange={(v) => setForm({ ...form, isActive: v })}
            />
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
            <button
              onClick={() => setModalOpen(false)}
              className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!confirm}
        onConfirm={doDelete}
        onCancel={() => setConfirm(null)}
        message={`Delete signing vendor "${confirm?.name}"?`}
      />
    </div>
  );
};

export default SigningVendors;
